import { create } from "zustand";
import { fetchAuthSession } from "@aws-amplify/auth";
import {
  extractUiChunkEnvelope,
  trackChunkTypeFrequency,
} from "../../utils/ChatEventReducer";
import { DefaultChatTransport } from "./conversation/transport/DefaultChatTransport";
import { validateUiChunkEnvelope } from "./conversation/stream/chunkSchema";
import {
  applyUiChunk,
  createUiChunkSessionState,
} from "./conversation/stream/processUiChunkStream";
import config from "../../config";
import { log as _log, critical as _crit } from "../../utils/ChatStreamLogger";

let _abortController = null;
let _subscribedSessionId = null;
let _activeTransport = null;
let _agentAbortController = null;
let _typedChunkCounters = {};
let _pollIntervalId = null;
let _visibilityHandler = null;

/** Map<agentId, AbortController> for multi-agent activity SSE subscriptions */
const _activityAbortControllers = new Map();

function _clearPolling() {
  if (_pollIntervalId) { clearInterval(_pollIntervalId); _pollIntervalId = null; }
  if (_visibilityHandler) { document.removeEventListener('visibilitychange', _visibilityHandler); _visibilityHandler = null; }
}
const _typedUiChunkStateBySessionId = new Map();
const _typedUiChunkErrorsBySessionId = new Map();
const MAX_TYPED_UI_CHUNK_ERRORS = 20;

function _normalizeSessionKey(chatSessionId) {
  return String(chatSessionId || "").trim();
}

function _getTypedUiChunkState(chatSessionId) {
  const key = _normalizeSessionKey(chatSessionId);
  if (!key) return createUiChunkSessionState("");
  const existing = _typedUiChunkStateBySessionId.get(key);
  if (existing) return existing;
  const fresh = createUiChunkSessionState(key);
  _typedUiChunkStateBySessionId.set(key, fresh);
  return fresh;
}

function _setTypedUiChunkState(chatSessionId, nextState) {
  const key = _normalizeSessionKey(chatSessionId);
  if (!key) return;
  _typedUiChunkStateBySessionId.set(key, nextState);
}

function _pushTypedUiChunkError(chatSessionId, error) {
  const key = _normalizeSessionKey(chatSessionId);
  if (!key || !error) return;
  const current = _typedUiChunkErrorsBySessionId.get(key) || [];
  const next = [...current, error];
  if (next.length > MAX_TYPED_UI_CHUNK_ERRORS) {
    next.splice(0, next.length - MAX_TYPED_UI_CHUNK_ERRORS);
  }
  _typedUiChunkErrorsBySessionId.set(key, next);
}

function clearTypedUiChunkState(chatSessionId) {
  const key = _normalizeSessionKey(chatSessionId);
  if (!key) return;
  _typedUiChunkStateBySessionId.delete(key);
  _typedUiChunkErrorsBySessionId.delete(key);
}

function toCanonicalMessage(typedMsg) {
  if (!typedMsg || typeof typedMsg !== "object") return null;
  return {
    ...typedMsg,
    role: typedMsg.role || "assistant",
  };
}

const useChatStreamStore = create((set, get) => ({
  sessionStreamStateBySessionId: {},
  streamError: null,

  _setSessionStreamState: (chatSessionId, nextState) => {
    const normalizedSessionId = _normalizeSessionKey(chatSessionId);
    if (!normalizedSessionId) return;
    const normalizedState = String(nextState || "").trim() || "idle";
    set((s) => {
      const cur =
        s.sessionStreamStateBySessionId[normalizedSessionId] || "idle";
      if (cur === normalizedState) return {};
      const level = normalizedState === "disconnected" ? "error"
        : normalizedState === "reconnecting" ? "warn"
        : "info";
      _crit("StreamStore", level, `session=${normalizedSessionId} connState ${cur} → ${normalizedState}`);
      return {
        sessionStreamStateBySessionId: {
          ...s.sessionStreamStateBySessionId,
          [normalizedSessionId]: normalizedState,
        },
      };
    });
  },

  handleUiChunk: (eventName, data, chatSessionId) => {
    trackChunkTypeFrequency("ui_chunk", _typedChunkCounters);

    const envelope = extractUiChunkEnvelope(eventName, data);
    const validation = validateUiChunkEnvelope(envelope);
    if (!validation.ok) {
      _log("StreamStore", `INVALID ui_chunk envelope session=${chatSessionId}: ${validation.error}`, { eventName, data });
      const error = {
        code: "INVALID_UI_CHUNK_ENVELOPE",
        message: validation.error,
        eventName: eventName || "ui_chunk",
        at: new Date().toISOString(),
      };
      _pushTypedUiChunkError(chatSessionId, error);
      return null;
    }

    const { chunk } = validation.value;
    trackChunkTypeFrequency(`ui_chunk:${chunk.type}`, _typedChunkCounters);

    const previousTypedState = _getTypedUiChunkState(chatSessionId);
    const nextTypedState = applyUiChunk(previousTypedState, chunk);
    _setTypedUiChunkState(chatSessionId, nextTypedState);

    if (nextTypedState.lastError) {
      _pushTypedUiChunkError(chatSessionId, nextTypedState.lastError);
    }

    const currentMsgId = nextTypedState.currentMessageId;
    const msgs = nextTypedState.messages || [];
    if (chunk.type === "finish") {
      return { _allMessages: msgs };
    }
    if (currentMsgId) {
      return msgs.find((m) => m.id === currentMsgId) || msgs[msgs.length - 1] || null;
    }
    return msgs[msgs.length - 1] || null;
  },

  getStreamMessages: (chatSessionId) => {
    const state = _getTypedUiChunkState(chatSessionId);
    return state?.messages || [];
  },

  clearStreamState: (chatSessionId) => {
    clearTypedUiChunkState(chatSessionId);
  },

  resetChunkCounters: () => {
    _typedChunkCounters = {};
  },

  disconnectStream: () => {
    const subscribedSessionId = _subscribedSessionId;
    if (subscribedSessionId) {
      _crit("StreamStore", "info", `disconnectStream session=${subscribedSessionId}`);
    }
    _clearPolling();
    if (_activeTransport) {
      _activeTransport.disconnect();
      _activeTransport = null;
    }
    if (_abortController) {
      _abortController.abort();
      _abortController = null;
    }
    if (subscribedSessionId) {
      get()._setSessionStreamState(subscribedSessionId, "idle");
      clearTypedUiChunkState(subscribedSessionId);
    }
    _subscribedSessionId = null;
  },

  disconnectAgentStream: () => {
    if (_agentAbortController) {
      _agentAbortController.abort();
      _agentAbortController = null;
    }
  },

  /**
   * Subscribe to agent-level SSE for multiple agents to track session activity.
   * Calls onActivity(agentId, eventType, payload) for new_session, status_changed, session_complete.
   * Manages one SSE connection per agent; call disconnectAllAgentActivity to tear down.
   */
  subscribeToAgentActivity: (agentIds, onActivity) => {
    // Disconnect agents no longer in the list
    for (const [id, ctrl] of _activityAbortControllers) {
      if (!agentIds.includes(id)) {
        ctrl.abort();
        _activityAbortControllers.delete(id);
      }
    }
    // Connect new agents
    for (const agentId of agentIds) {
      if (_activityAbortControllers.has(agentId)) continue;
      const abortController = new AbortController();
      _activityAbortControllers.set(agentId, abortController);

      (async () => {
        while (!abortController.signal.aborted) {
          try {
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();
            const url = `${config.AI_SERVICE_BASE}/private/ai/agent/${agentId}/events`;
            const response = await fetch(url, {
              headers: { Authorization: `Bearer ${token}` },
              signal: abortController.signal,
            });
            if (!response.ok) {
              throw new Error(`SSE failed: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let eventType = "";
            let dataStr = "";

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n");
              buffer = lines.pop();
              for (const line of lines) {
                if (line.startsWith("event:")) {
                  eventType = line.slice(6).trim();
                } else if (line.startsWith("data:")) {
                  dataStr = line.slice(5).trim();
                } else if (line === "" && eventType) {
                  if (eventType === "new_session" || eventType === "session_activity") {
                    let payload = null;
                    try { payload = JSON.parse(dataStr); } catch (_) {}
                    if (onActivity) onActivity(agentId, eventType, payload);
                  }
                  eventType = "";
                  dataStr = "";
                }
              }
            }
          } catch (e) {
            if (abortController.signal.aborted) return;
            await new Promise((r) => setTimeout(r, 3000));
          }
        }
      })();
    }
  },

  disconnectAllAgentActivity: () => {
    for (const [, ctrl] of _activityAbortControllers) {
      ctrl.abort();
    }
    _activityAbortControllers.clear();
  },

  subscribeToAgent: async (agentId, onNewSession) => {
    get().disconnectAgentStream();
    const abortController = new AbortController();
    _agentAbortController = abortController;

    while (!abortController.signal.aborted) {
      try {
        const { tokens } = await fetchAuthSession();
        const token = tokens.idToken.toString();
        const url = `${config.AI_SERVICE_BASE}/private/ai/agent/${agentId}/events`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });
        if (!response.ok) throw new Error(`SSE failed: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop();
          let eventType = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            if (line.startsWith("data:") && eventType === "new_session") {
              if (onNewSession) onNewSession(agentId);
              eventType = "";
            }
          }
        }
      } catch (e) {
        if (abortController.signal.aborted) return;
        await new Promise((r) => setTimeout(r, 3000));
      }
    }
  },

  /**
   * Subscribe to session SSE events.
   * @param {string} connectId — chatSessionId for private, publicSessionToken for public
   * @param {string} chatSessionId — always the real chatSessionId (used as state key)
   * @param {object} options — { reconnectWhileSubscribed, onEvent, shouldReconnect, skipAuth }
   */
  subscribeToSession: async (connectId, chatSessionId, options = {}) => {
    if (_subscribedSessionId === chatSessionId && _activeTransport) {
      const forceReconnect = options?.forceReconnect === true;
      if (!forceReconnect) {
        _log("StreamStore", `subscribeToSession: already subscribed to ${chatSessionId}, skipping`);
        _setTypedUiChunkState(chatSessionId, createUiChunkSessionState(chatSessionId));
        _typedUiChunkErrorsBySessionId.set(String(chatSessionId), []);
        return { aborted: false, connectionFailed: false };
      }
      _crit("StreamStore", "warn", `subscribeToSession: forceReconnect ${chatSessionId} — tearing down old transport`);
      get().disconnectStream();
    }

    const isSessionTerminal = options?.isSessionTerminal;
    if (typeof isSessionTerminal === 'function' && isSessionTerminal()) {
      return { aborted: false, connectionFailed: false };
    }

    get().disconnectStream();
    const reconnectWhileSubscribed = options?.reconnectWhileSubscribed === true;
    const onEvent = options?.onEvent;
    const shouldReconnectFn = options?.shouldReconnect;
    const skipAuth = options?.skipAuth === true;
    const onPollFallback = options?.onPollFallback;
    const pollIntervalMs = options?.pollIntervalMs || 3000;
    const isSessionActive = options?.isSessionActive;

    const abortController = new AbortController();
    _subscribedSessionId = chatSessionId;
    _abortController = abortController;
    _crit("StreamStore", "info", `subscribeToSession session=${chatSessionId} public=${skipAuth} reconnectWhileSubscribed=${reconnectWhileSubscribed}`);
    get()._setSessionStreamState(chatSessionId, "connecting");
    set({ streamError: null });
    _setTypedUiChunkState(chatSessionId, createUiChunkSessionState(chatSessionId));
    _typedUiChunkErrorsBySessionId.set(String(chatSessionId), []);

    // Start polling fallback + visibility recovery
    _clearPolling();
    if (typeof onPollFallback === 'function') {
      _pollIntervalId = setInterval(() => {
        const active = typeof isSessionActive === 'function' ? isSessionActive() : true;
        if (!active) { _clearPolling(); return; }
        const state = get().sessionStreamStateBySessionId[chatSessionId] || 'idle';
        if (state !== 'connected') {
          onPollFallback(chatSessionId);
        }
      }, pollIntervalMs);

      _visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          const active = typeof isSessionActive === 'function' ? isSessionActive() : true;
          if (active) onPollFallback(chatSessionId);
        }
      };
      document.addEventListener('visibilitychange', _visibilityHandler);
    }

    const transport = new DefaultChatTransport({
      baseUrl: config.AI_SERVICE_BASE,
      skipAuth,
      onEvent: (eventName, data) => {
        if (onEvent) onEvent(eventName, data, chatSessionId);
      },
      onConnectionStateChange: (nextState) => {
        if (_subscribedSessionId !== chatSessionId) return;
        get()._setSessionStreamState(chatSessionId, nextState);
      },
      onStreamDiagnostic: (info) => {
        trackChunkTypeFrequency(`diag:${info.event}`, _typedChunkCounters);
      },
    });
    _activeTransport = transport;
    const result = await transport.connect(connectId, {
      shouldReconnect: () => {
        const should = typeof shouldReconnectFn === "function" ? shouldReconnectFn() : true;
        if (!should) return false;
        if (reconnectWhileSubscribed) return true;
        return false;
      },
      maxRetries: 5,
    });
    if (!result.connectionFailed) {
      _clearPolling();
    }

    if (result.aborted) { _clearPolling(); return { aborted: true }; }

    if (_abortController === abortController) {
      _abortController = null;
    }
    if (result.connectionFailed && _subscribedSessionId === chatSessionId) {
      _crit("StreamStore", "error", `subscribeToSession session=${chatSessionId} connectionFailed — stream lost`);
      get()._setSessionStreamState(chatSessionId, "disconnected");
      set({ streamError: "Connection lost. Messages may be delayed." });
    }

    if (_activeTransport === transport) {
      _activeTransport = null;
    }
    if (_subscribedSessionId === chatSessionId && !result.connectionFailed) {
      _subscribedSessionId = null;
    }

    return result;
  },

  getSubscribedSessionId: () => _subscribedSessionId,
}));

export {
  toCanonicalMessage,
  clearTypedUiChunkState,
};
export default useChatStreamStore;
